import { Box } from '@mui/material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { DURATION, fadeInOnce } from '@/shared/styles/motion'
import { scaledFontSize } from '@/shared/styles/typography'

interface BrandMarkProps {
  /** 起動モーション (左面 → 右面の順に灯る) を再生するか。 判定は呼び出し側の useBootReveal(). */
  boot?: boolean
}

/**
 * サイドバー最上段のブランドマーク (design-book 02-components/sidebar.md WorkspaceRow)。
 *
 * 菱形を左右二面に割った**面取り** (ADR-0026)。 色相は増やさず `fg.default` の
 * opacity 0.45 / 1.0 で組む。 起動時はこの二面が左 → 右の順に灯る (opacity のみ。
 * 出現の演出なので reduced-motion では止める)。
 *
 * - **面 (背景) を持たない**。 `bg.emphasis` は行 hover の地として意味が確定しており、
 *   静的な面に流用すると hover と区別がつかない (かつ bg.subtle 上 1.09:1 で見えない)。
 *   面のための新色追加は ADR-0002 により禁止。
 * - 彩度は上げない。 `accent.primary` は CTA / focus 専用 (ADR-0024 で再提案禁止)。
 * - 辺の長さは `label` 字送りに追従する (ADR-0019 の文字サイズモード。 サイドバー幅が
 *   `224 × fontScale` で広がるのにマークだけ据え置くと、 拡大時に相対的に消える)。
 */
export const BrandMark = ({ boot = false }: BrandMarkProps) => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{
      width: scaledFontSize('label'),
      height: scaledFontSize('label'),
      flexShrink: 0,
      display: 'inline-flex',
      color: colorTokens.fg.default,
      ...(boot
        ? {
            '& [data-facet="left"]': fadeInOnce(DURATION.base),
            '& [data-facet="right"]': fadeInOnce(DURATION.base, DURATION.fast),
          }
        : {}),
    }}
  >
    <svg width="100%" height="100%" viewBox="0 0 10 10" focusable="false">
      <path
        data-facet="left"
        d="M5 0.6 L0.6 5 L5 9.4 Z"
        fill="currentColor"
        opacity="0.45"
      />
      <path data-facet="right" d="M5 0.6 L9.4 5 L5 9.4 Z" fill="currentColor" />
    </svg>
  </Box>
)
